/**
 * Translation context creation utilities
 */

import type { TranslationContext, ParsedNamespaceKey } from '../types/translation.types.js';

/**
 * Create translation context with namespace resolution
 * 
 * @param parsedKey - Parsed namespace key information
 * @param currentLocale - Current locale
 * @param defaultNamespace - Default namespace if none specified
 * @returns Complete translation context
 */
export function createTranslationContext(
  parsedKey: ParsedNamespaceKey,
  currentLocale: string,
  defaultNamespace?: string
): TranslationContext {
  // Use explicit namespace if provided, otherwise use default
  const effectiveNamespace = parsedKey.hasExplicitNamespace
    ? parsedKey.namespace
    : defaultNamespace;

  return {
    locale: currentLocale,
    namespace: effectiveNamespace
  };
}

/**
 * Check if two contexts are equivalent (for caching)
 * 
 * @param context1 - First context
 * @param context2 - Second context  
 * @returns Whether contexts are equivalent
 */
export function areContextsEquivalent(
  context1: TranslationContext, 
  context2: TranslationContext
): boolean {
  return (
    context1.locale === context2.locale &&
    context1.namespace === context2.namespace
  );
}